import React, { useState, useEffect } from 'react';
import classes from './TextLeveling.module.css';
import util from '../../styles/utilities.module.css';
import criteriaClasses from '../CriteriaSubmission/criteriaSubmission.module.css';
import { useGenerateTextLevelingMutation } from '../../slices/authApi';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from 'react-redux';
import { selectTextID, setDefaultValues } from '../../slices/form';
import { Tooltip } from "@mui/material";
import { useAppUsageLogger } from "../../slices/authApi";
import '../../common/common_styles.css';
import ActionButton from '../UI/ActionButton/ActionButton';
import { useNavigate } from 'react-router-dom';
import ModalWrapper from '../UI/modal_wrapper/modal_wrapper';
import { useSaveExportTextLevelingMutation } from '../../slices/authApi';
import SignInInfoModal from '../UI/signin_info_modal/signin_info_modal';
import openGoogleLogin from '../../common/openGoogleLogin';
import Spinner from '../UI/spinner/spinner';

const TextLevelingTool = ({ onGenerateClick }) => {
    const [inputText, setInputText] = useState('');
    const [inputWordCount, setInputWordCount] = useState(0);
    const [inputCharCount, setInputCharCount] = useState(null);

    const [selectedLevelType, setSelectedLevelType] = useState('grade');
    const [selectedLevel, setSelectedLevel] = useState('');
    const [targetDifficulty, setTargetDifficulty] = useState('medium');
    const [lastSelectedGrade, setLastSelectedGrade] = useState('');
    const [lastSelectedDifficulty, setLastSelectedDifficulty] = useState('medium');
    const [outputText, setOutputText] = useState('');
    const [isFirstGeneration, setIsFirstGeneration] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [exportError, setExportError] = useState(false);
    const [copySuccess, setCopySuccess] = useState('');
    const dispatch = useDispatch();
    const logAppUse = useAppUsageLogger();
    
    const [originalLexileLevel, setOriginalLexileLevel] = useState('');

    const navigate = useNavigate();
    const textID = useSelector(selectTextID);
    const [saveExportTextLeveling, { isLoading: isSaveExportTextLevelingLoading }] = useSaveExportTextLevelingMutation();
    const [backToHomeModal, setBackToHomeModal] = useState(false);
    const [infoModal, setInfoModal] = useState(false);
    const [exportedUrl, setExportedUrl] = useState("");

    useEffect(() => {
        if (exportedUrl) {
            window.open(exportedUrl, "_blank", "noreferrer");
            setExportedUrl("");
        }
    }, [exportedUrl]);

    const gradeLevelOptions = [
        { value: 'k', label: 'Kindergarten' },
        { value: '1', label: 'Grade 1' },
        { value: '2', label: 'Grade 2' },
        { value: '3', label: 'Grade 3' },
        { value: '4', label: 'Grade 4' },
        { value: '5', label: 'Grade 5' },
        { value: '6', label: 'Grade 6' },
        { value: '7', label: 'Grade 7' },
        { value: '8', label: 'Grade 8' },
        { value: '9', label: 'Grade 9' },
        { value: '10', label: 'Grade 10' },
        { value: '11', label: 'Grade 11' },
        { value: '12', label: 'Grade 12' },
    ];

    const lexileLevelOptions = [
        { value: '100L', label: '100L' },
        { value: '200L', label: '200L' },
        { value: '300L', label: '300L' },
        { value: '400L', label: '400L' },
        { value: '500L', label: '500L' },
        { value: '600L', label: '600L' },
        { value: '700L', label: '700L' },
        { value: '800L', label: '800L' },
        { value: '900L', label: '900L' },
        { value: '1000L', label: '1000L' },
        { value: '1100L', label: '1100L' },
        { value: '1200L', label: '1200L' },
        { value: '1300L', label: '1300L' }
    ];

    const gradeToLexileMap = {
        'k': 100,
        '1': 300,
        '2': 500,
        '3': 650,
        '4': 850,
        '5': 920,
        '6': 1000,
        '7': 1050,
        '8': 1100,
        '9': 1150,
        '10': 1200,
        '11': 1275,
        '12': 1300,
    };

    const findClosestLexileOption = (lexileValue) => {
        if (lexileValue <= 100) return 'BR';

        const lexileOptions = lexileLevelOptions
            .map(opt => opt.value)
            .filter(opt => opt !== 'BR' && opt !== '1500L+')
            .map(opt => parseInt(opt.replace('L', '')))
            .sort((a, b) => a - b);

        let closestOption = lexileOptions[0];
        for (const option of lexileOptions) {
            if (option <= lexileValue) {
                closestOption = option;
            } else {
                break;
            }
        }

        return `${closestOption}L`;
    };

    const gradeToLexile = (grade, difficulty = 'medium') => {
        const gradeIndex = gradeLevelOptions.findIndex(option => option.value === grade);

        if (gradeIndex === -1) return null;

        let adjustedIndex = gradeIndex;
        if (difficulty === 'easy') {
            adjustedIndex = Math.max(0, gradeIndex - 2);
        } else if (difficulty === 'hard') {
            adjustedIndex = Math.min(gradeLevelOptions.length - 1, gradeIndex + 2);
        }

        const adjustedGradeValue = gradeLevelOptions[adjustedIndex].value;
        const lexileValue = gradeToLexileMap[adjustedGradeValue];

        if (lexileValue === undefined) return null;
        return findClosestLexileOption(lexileValue);
    };

    const [generateTextLeveling] = useGenerateTextLevelingMutation();

    const handleInputChange = (event) => {
        let inputValue = event.target.value;
        const inputWords = inputValue?.split(/\s+/).filter(function (word) {
            return word.length > 0;
        });

        setInputWordCount(inputWords.length);
        setInputCharCount(null);

        if (inputWords.length > 10000) {
            inputValue = inputWords.slice(0, 10000).join(" ");
            setInputWordCount(inputWords.slice(0, 10000).length);
            setInputCharCount(inputValue.length);
        }

        setInputText(inputValue);
    };

    const handleFileUpload = (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        logAppUse("text_leveling", "file_upload_attempt");
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            logAppUse("text_leveling", "file_upload_success");
            const content = e.target.result;
            setInputText(content);

            const words = content.split(/\s+/).filter(function (word) {
                return word.length > 0;
            });

            setInputWordCount(words.length > 10000 ? 10000 : words.length);

            if (words.length > 10000) {
                const truncatedContent = words.slice(0, 10000).join(" ");
                setInputText(truncatedContent);
                setInputCharCount(truncatedContent.length);
            }
        };

        reader.onerror = () => {
            logAppUse("text_leveling", "file_upload_error");
            setError('Error reading file. Please try again.');
        };

        if (file.type === 'text/plain') {
            reader.readAsText(file);
        } else {
            setError('Only .txt files can be processed directly. For other file types, please copy and paste the text manually.');
        }

        // Delay clearing the input value to avoid race conditions
        setTimeout(() => {
            event.target.value = "";
        }, 100);
    };

    const handleLevelTypeChange = (event) => {
        logAppUse("text_leveling", "level_type_change");
        const newLevelType = event.target.value;
        const oldLevelType = selectedLevelType;

        if (newLevelType === oldLevelType) {
            return;
        }

        if (newLevelType === 'lexile') {
            const gradeToConvert = lastSelectedGrade || selectedLevel;
            const difficultyToUse = lastSelectedDifficulty;
            if (gradeToConvert) {
                const newLexileLevel = gradeToLexile(gradeToConvert, difficultyToUse);
                setSelectedLevel(newLexileLevel || '');
            } else {
                setSelectedLevel('');
            }
        } else if (newLevelType === 'grade') {
            setSelectedLevel(lastSelectedGrade);
            setTargetDifficulty(lastSelectedDifficulty);
        }

        setSelectedLevelType(newLevelType);
    };

    const handleLevelChange = (event) => {
        logAppUse("text_leveling", "level_change");
        const newLevel = event.target.value;
        setSelectedLevel(newLevel);
        if (selectedLevelType === 'grade') {
            setLastSelectedGrade(newLevel);
        }
    };

    const handleDifficultyChange = (event) => {
        logAppUse("text_leveling", "difficulty_change");
        const newDifficulty = event.target.value;
        setTargetDifficulty(newDifficulty);
        setLastSelectedDifficulty(newDifficulty);
    };

    const handleGenerateSubmit = async (event) => {
        event.preventDefault();
        logAppUse("text_leveling", isFirstGeneration ? "generate" : "regenerate");

        if (!inputText.trim()) {
            setError('Please enter some text to adjust');
            return;
        }

        if (!selectedLevel) {
            setError('Please select a target level');
            return;
        }

        setError('');
        setIsLoading(true);
        setOriginalLexileLevel('');

        try {
            const payload = {
                text: inputText,
                target_level: selectedLevel,
                level_type: selectedLevelType
            };

            if (selectedLevelType === 'grade') {
                payload.target_difficulty = targetDifficulty;
            }

            const response = await generateTextLeveling(payload).unwrap();

            setOutputText(response.leveled_text || 'Error: No text returned from the server');
            setOriginalLexileLevel(response.original_lexile_level || '');

            setIsFirstGeneration(false);

        } catch (err) {
            logAppUse("text_leveling", "generate_error");
            console.error('Failed to adjust text complexity:', err);
            setError(err.data?.message || 'An error occurred while adjusting the text. Please try again.');
            setOutputText('');
            setOriginalLexileLevel('');
        } finally {
            setIsLoading(false);
        }

        if (onGenerateClick) {
            onGenerateClick();
        }
    };

    const handleCopyText = () => {
        logAppUse("text_leveling", "copy_adjusted_text");
        if (outputText) {
            navigator.clipboard.writeText(outputText)
                .then(() => {
                    setCopySuccess('Copied!');
                    setTimeout(() => setCopySuccess(''), 2000);
                })
                .catch(err => {
                    console.error('Failed to copy text:', err);
                    setCopySuccess('Failed to copy');
                });
        }
    };

    const handleExportToGoogleDocs = async () => {
        logAppUse("text_leveling", "export_to_google_docs");
        const exportResponse = await saveAndExportEndpoint();

        if (exportResponse?.data?.status === 200) {
            localStorage.setItem("preserveData", null);
            setExportedUrl(exportResponse?.data?.url);
        } else {
            // Only set export error if the status is 500, otherwise prompt for sign-in
            setExportError(exportResponse?.data?.status === 500);
            setInfoModal(true);
        }
    };

    const saveAndExportEndpoint = async () => {
        try {
            const saveExportResponse = await saveExportTextLeveling({
                export_format: "Google Docs",
                adjusted_text: outputText,
                tool_type: "text_leveling",
                mc_questions: [],
                ff_questions: [],
                tf_questions: [],
                fb_questions: [],
                text_id: textID || 0,
                quiz_id: 0,
                include_text: true,
                submit_for_sharing: false
            });
            return saveExportResponse;
        } catch (err) {
            console.log({ err });
        }
    };

    const signInInfoContinueHandler = () => {
        logAppUse("text_leveling", "signin_info_continue");
        localStorage.setItem(
            "preserveData",
            JSON.stringify({
                sourcePage: "textLevelingTool",
                exportedText: outputText,
                textID
            })
        );
        openGoogleLogin();
    };

    const backToHomeHandler = () => {
        logAppUse("text_leveling", "back_to_home_click");
        setBackToHomeModal(true);
    };

    const backToHomeCancelBtnHandler = () => {
        logAppUse("text_leveling", "back_to_home_cancel");
        setBackToHomeModal(false);
    };

    const backToHomeConfirmBtnHandler = async () => {
        await logAppUse("text_leveling", "back_to_home_confirm");
        localStorage.removeItem("from");
        setBackToHomeModal(false);
        dispatch(setDefaultValues());
        navigate("/toolSelection");
    };

    return (
        <div className={classes.textLevelingContainer}>
            {isSaveExportTextLevelingLoading && (
                <Spinner message="We are creating your exported file." />
            )}
            <div className={criteriaClasses["input-container"]}>
                <label className={criteriaClasses.formLabel} htmlFor="text">Original Text:</label>
                <div className={criteriaClasses["textAndFileContainer"]}>
                    <div style={{ height: "250px" }} className={criteriaClasses.textAreaContainer}>
                        <textarea
                            style={{ resize: "none" }}
                            name="text"
                            id="text"
                            className={`${criteriaClasses.textAreaNoBorder} ${inputWordCount === 10000 ? criteriaClasses.textAreaMax : ""
                                }`}
                            onChange={handleInputChange}
                            maxLength={inputCharCount}
                            placeholder="Paste your text here"
                            value={inputText}
                        />
                        <div className={criteriaClasses.wordCount}>({inputWordCount}/10000)</div>
                    </div>
                    <div style={{ margin: "0px 20px 0px 20px" }}>
                        <div className={`hideFileIcon`}>
                            <input
                                style={{ display: "none", border: "1px solid black" }}
                                type="file"
                                id="fileUpload"
                                name="fileUpload"
                                onChange={handleFileUpload}
                                accept=".txt"
                                className="border hidden"
                            />
                            <label>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                    }}
                                >
                                    <div
                                        className="uploadButton"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById('fileUpload').click();
                                        }}
                                    >
                                        Upload file
                                    </div>
                                    <div style={{ textAlign: "start" }}>
                                        <p style={{ fontWeight: "bold" }}>(.txt file only)</p>
                                        <p style={{ fontSize: "12px", fontWeight: "300" }}>
                                            Note: You can also paste your text directly in the text area.
                                        </p>
                                    </div>
                                </div>
                            </label>
                            {error && error.includes('Only .txt files') && (
                                <p
                                    style={{
                                        color: "red",
                                        textAlign: "left",
                                        fontWeight: "600",
                                    }}
                                >
                                    {error}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.levelSelectorContainer}>
                <div className={classes.levelTypeRadioGroup}>
                    <label className={`${criteriaClasses.formLabel}`}>Select Level Type:</label>
                    <div className={classes.radioOptions}>
                        <label className={classes.radioLabel}>
                            <input
                                type="radio"
                                name="levelType"
                                value="grade"
                                checked={selectedLevelType === 'grade'}
                                onChange={handleLevelTypeChange}
                                className={classes.radioInput}
                            />
                            Grade Level
                        </label>
                        <label className={classes.radioLabel}>
                            <input
                                type="radio"
                                name="levelType"
                                value="lexile"
                                checked={selectedLevelType === 'lexile'}
                                onChange={handleLevelTypeChange}
                                className={classes.radioInput}
                            />
                            <Tooltip
                                title={
                                    `Lexile® is a registered trademark of MetaMetrics, Inc. ` +
                                    `All Lexile® estimates are AI-generated ` +
                                    `approximations and not official MetaMetrics measures.`
                                }
                            >   <span>
                                    <span style={{ textDecoration: 'underline', cursor: 'help' }}>
                                    Lexile
                                    </span>
                                    <span style={{ marginLeft: '0.2ch', marginRight: '0.75ch' }}>®</span>
                                </span>
                            </Tooltip>Level
                        </label>
                    </div>
                </div>

                <div className={classes.levelSelector}>
                    <div className={classes.levelRow}>
                        <div className={classes.levelSelectWrapper}>
                            <label className={criteriaClasses.formLabel} htmlFor="level">Target {selectedLevelType === 'grade' ? 'Grade' : 'Lexile'} Level:</label>
                            <select
                                id="level"
                                value={selectedLevel}
                                onChange={handleLevelChange}
                                className={classes.levelSelect}
                            >
                                <option value="">Choose a level</option>
                                {selectedLevelType === 'grade'
                                    ? gradeLevelOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))
                                    : lexileLevelOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className={classes.levelSelectWrapper}>
                            {selectedLevelType === 'grade' && (
                                <>
                                    <label className={criteriaClasses.formLabel} htmlFor="difficulty">Difficulty:</label>
                                    <select
                                        id="difficulty"
                                        value={targetDifficulty}
                                        onChange={handleDifficultyChange}
                                        className={classes.levelSelect}
                                    >
                                        <option value="easy">Easy</option>
                                        <option value="medium">Medium</option>
                                        <option value="hard">Hard</option>
                                    </select>
                                </>
                            )}
                        </div>
                    </div>

                    <div className={classes.generateButtonContainer}>
                        <ActionButton
                            text={isFirstGeneration ? 'Generate' : 'Regenerate'}
                            handleOnClick={handleGenerateSubmit}
                            isDisabled={isLoading}
                            variant="secondary"
                        />
                    </div>
                    {isLoading && (
                        <div className={classes.loadingContainer}>
                            <CircularProgress size={24} />
                            <span className={classes.loadingText}>Generating...</span>
                        </div>
                    )}
                    {false && outputText && !isFirstGeneration && (
                        <div className={classes.additionalInfoContainer}>
                            {originalLexileLevel && (
                                <p className={classes.additionalInfoText}>
                                    Estimated{' '}
                                    <Tooltip
                                        title={
                                            `Lexile® is a registered trademark of MetaMetrics, Inc. ` +
                                            `This is an estimated range based on AI ` +
                                            `analysis, not an official Lexile® score.`
                                        }
                                    >
                                        <span>
                                        <span style={{ textDecoration: 'underline', cursor: 'help' }}>
                                            Lexile
                                        </span>
                                        <span style={{ marginLeft: '0.2ch', marginRight: '0.75ch' }}>®</span>
                                        </span>
                                    </Tooltip>Level of original text: {originalLexileLevel}
                                </p>
                            )}
                        </div>
                    )}
                </div>

            </div>

            {error && !error.includes('Only .txt files') && <div className={classes.errorMessage}>{error}</div>}

            <div className={criteriaClasses["input-container"]}>
                <label className={criteriaClasses.formLabel} htmlFor="outputText" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    Adjusted Text:
                    {outputText && (
                        <button
                            className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']}`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                height: 'auto',
                                lineHeight: 1.1,
                                padding: '6px 12px'
                            }}
                            type="button"
                            onClick={handleCopyText}
                            title="Copy to clipboard"
                        >
                            <ContentCopyIcon fontSize="small" />
                            {copySuccess ? copySuccess : "Copy Text"}
                        </button>
                    )}
                </label>

                <div style={{ height: "250px", position: "relative" }} className={classes.textAreaContainer}>
                    <textarea
                        style={{ resize: "none" }}
                        name="outputText"
                        id="outputText"
                        className={criteriaClasses.textArea}
                        readOnly
                        placeholder="Adjusted text will appear here"
                        value={outputText}
                    />

                </div>

            </div>

            
               
                    <div className={`${classes['form-actions']} ${classes['form-actions--terminal']}`}>
                    {outputText && !isFirstGeneration && (
                        <ActionButton
                            text="Export to Google Docs"
                            subText="(A ready-to-use document will open in a new tab)"
                            handleOnClick={handleExportToGoogleDocs}
                            isDisabled={!outputText || isLoading || isSaveExportTextLevelingLoading}
                            variant='secondary'
                        />
                   )}

                        <ActionButton
                            text="Back to Home"
                            handleOnClick={backToHomeHandler}
                            isDisabled={isLoading || isSaveExportTextLevelingLoading}
                            variant='secondary'
                        />
                    </div>
               


            <ModalWrapper
                modalProps={{
                    isOpen: backToHomeModal,
                    description: `Are you sure you want to leave this page? Your current work will be lost if not exported.`,
                    leftBtnHandler: backToHomeCancelBtnHandler,
                    rightBtnHandler: backToHomeConfirmBtnHandler,
                    buttons: {
                        leftBtn: "Cancel",
                        rightBtn: "Confirm",
                    },
                }}
            />
            <SignInInfoModal
                isOpen={infoModal}
                cancelHandler={() => setInfoModal(false)}
                continueHandler={signInInfoContinueHandler}
                error={exportError}
            />
        </div>
    );
};

export default TextLevelingTool;